import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';

import { BackLink, useParams } from '../Navigation';
import {
  ApolloError,
  Button,
  Heading,
  HStack,
  LoadingIndicator,
  ScrollPane,
  SimpleDrawer,
  Text,
  VStack,
} from '../../core-ui';

import EmailPreview from './EmailPreview';
import RecipientsDialog from './RecipientsDialog';

import {
  GetJobSummary,
  GetJobSummaryVariables,
  GetJobSummary_job as Job,
} from './__generated__/GetJobSummary';

const GET_JOB_SUMMARY = gql`
  query GetJobSummary($jobId: String!) {
    job(id: $jobId) {
      id
      email {
        id
        subject
        status
      }
      recipients {
        id
        name
        email
        vendor
        status
      }
      rendered {
        type
        value
      }
      stats {
        total
        processed
        failed
      }
    }
  }
`;

function SentSummaryView({ job }: { job: Job }) {
  const { email, recipients, rendered, stats } = job;
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);

  return (
    <VStack alignItems="stretch" flex={1} minH={0}>
      <VStack alignItems="stretch" px={10} pt={2}>
        <BackLink to={['/mailer/emails']} text={t('Back to email list')} />
        <HStack>
          <Heading as="h2" size="lg">
            {t('Sent Email')}
          </Heading>
        </HStack>
        <Text>
          {t.jsx('To: <link>{count} Recipient(s)</link>', {
            count: stats.total,
            link: (content) => (
              <Button variant="link" onClick={() => setShowRecipients(true)}>
                {content}
              </Button>
            ),
          })}
        </Text>
      </VStack>
      <ScrollPane contentContainerProps={{ px: 10, pb: 10 }}>
        <VStack align="flex-start">
          <Text>{t('Subject: {subject}', { subject: email.subject })}</Text>
          <Text>
            {t('Successfully Sent: {successful}', {
              successful: stats.processed - stats.failed,
            })}
          </Text>
          <Text>{t('Failed: {failed}', { failed: stats.failed })}</Text>
          <Button variant="link" onClick={() => setDrawerOpen(true)}>
            {t('View email')}
          </Button>
        </VStack>
      </ScrollPane>
      <RecipientsDialog
        isOpen={showRecipients}
        recipients={recipients}
        onClosePress={() => setShowRecipients(false)}
      />
      <SimpleDrawer
        drawerTitle={t('Email preview')}
        isOpen={isDrawerOpen}
        onClose={() => setDrawerOpen(false)}
        size="xl"
      >
        <EmailPreview height="100%" html={rendered.value} />
      </SimpleDrawer>
    </VStack>
  );
}

export default function SentSummaryPage() {
  const params = useParams();
  const jobId = params.id || '';
  const { error, data } = useQuery<GetJobSummary, GetJobSummaryVariables>(
    GET_JOB_SUMMARY,
    {
      variables: { jobId },
      fetchPolicy: 'network-only',
    },
  );
  if (error) {
    return <ApolloError error={error} />;
  }
  if (!data) {
    return <LoadingIndicator />;
  }
  if (!data.job) {
    return <Text>{t('Not Found')}</Text>;
  }
  return <SentSummaryView job={data.job} />;
}
